import { useContext, PropsWithChildren } from "react";
import Grid from "@mui/material/Grid2";
import { Link } from "react-router-dom";
import { differenceInMonths, isToday, subDays } from "date-fns";
import { Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "@common/components/Typography";
import DeviceLiveChart from "@common/components/LiveCharts/DeviceLiveChart";
import { useMeetingAnnotations } from "@common/components/LiveCharts/hooks/useMeetingAnnotations";
import { Device, MonitoringLevel } from "@services/devices/types";
import { GraphColor, GraphType } from "@services/monitoring/types";
import Button from "@common/components/Button";
import { FormContext } from "../../context/formContext";
import { toEndOfDay, toStartOfDay } from "@common/utils/localTime.utils";

type PaperDeviceProps = {
  device: Device;
};

const PaperDevice = (props: PropsWithChildren<PaperDeviceProps>) => {
  const { device } = props;
  const { t } = useTranslation();
  const { form } = useContext(FormContext);
  const { ticket } = form;

  const getStartTime = () => {
    if (
      ticket &&
      ticket.details &&
      ticket.details.events &&
      ticket.details.events.length > 0
    ) {
      const creation = ticket.details.events.reduce(
        (accumulator, currentValue) =>
          accumulator && accumulator.createDate > currentValue.createDate
            ? currentValue
            : accumulator
      );
      if (creation.createDate) {
        const createDate = new Date(Number(creation.createDate));
        const today = new Date();
        if (differenceInMonths(today, createDate) > 3) {
          return toStartOfDay(subDays(today, 30)).getTime();
        }

        return toStartOfDay(new Date(Number(creation.createDate))).getTime();
      }
    }

    return toStartOfDay(new Date()).getTime();
  };

  const getStopTime = () => {
    if (
      ticket &&
      ticket.details &&
      ticket.details.events &&
      ticket.details.events.length > 0
    ) {
      const creation = ticket.details.events.reduce(
        (accumulator, currentValue) =>
          accumulator && accumulator.createDate > currentValue.createDate
            ? currentValue
            : accumulator
      );
      if (creation.createDate) {
        const createDate = new Date(Number(creation.createDate));
        const today = new Date();
        if (differenceInMonths(today, createDate) > 3) {
          return toEndOfDay(subDays(today, 30)).getTime();
        }
        if (!isToday(createDate)) {
          return toEndOfDay(createDate).getTime();
        }
        return today.getTime();
      }
    }
    return toEndOfDay(new Date()).getTime();
  };

  const checkSubType = (value: string) => {
    return ticket?.issue?.subTypes?.find((subtype: string) => subtype == value);
  };

  const getTroubleshootGraph = () => {
    if (!device.apiSupported) {
      return GraphType.Ping_Latency;
    }
    if (
      checkSubType("Ping Latency") ||
      checkSubType("Link Down") ||
      checkSubType("Stale Monitoring Data")
    ) {
      return GraphType.Ping_Latency;
    }

    if (device.type == "Codecs") {
      // Video Endpoint
      // 1 Ping Latency
      // 2 Video Speed
      // 3 Call Rate
      // 4 Packet Loss
      // 5 Audio Jitter
      // 6 Video Jitter

      if (checkSubType("Packet Loss Rx") || checkSubType("Packet Loss Tx")) {
        return GraphType.Packet_Loss;
      } else if (
        checkSubType("Content Packet Loss Rx") ||
        checkSubType("Content Packet Loss Tx")
      ) {
        return GraphType.Packet_Loss_Content;
      } else if (
        checkSubType("Audio Jitter Rx") ||
        checkSubType("Audio Jitter Tx")
      ) {
        return GraphType.Jitter_Audio;
      } else if (
        checkSubType("Video Jitter Rx") ||
        checkSubType("Video Jitter Tx")
      ) {
        return GraphType.Jitter_Video;
      } else if (
        checkSubType("Content Jitter Rx") ||
        checkSubType("Content Jitter Tx")
      ) {
        return GraphType.Jitter_Content;
      } else if (
        checkSubType("Frame Rate Rx") ||
        checkSubType("Frame Rate Tx")
      ) {
        return GraphType.Frame_Rate;
      } else if (
        checkSubType("Content Frame Rate Rx") ||
        checkSubType("Content Frame Rate Tx")
      ) {
        return GraphType.Frame_Rate_Content;
      } else if (
        checkSubType("Resolution Height Rx") ||
        checkSubType("Resolution Height Tx")
      ) {
        return GraphType.Frame_Rate;
      } else if (
        checkSubType("Content Resolution Height Rx") ||
        checkSubType("Content Resolution Height Tx")
      ) {
        return GraphType.Frame_Rate_Content;
      }

      switch (ticket?.issue?.issue) {
        case "Screen Layout Issue":
        case "End User Issue":
        case "Camera Issue":
          return GraphType.Frame_Rate;
        case "Content Sharing":
          return GraphType.Packet_Loss_Content;
        case "Room Audio Issue":
        case "3rd Party Audio Bridge Issues":
        case "Audio Quality":
        case "Loss of Audio":
          return GraphType.Jitter_Audio;
        case "Video Quality":
        case "Loss of Video":
          return GraphType.Jitter_Video;
        case "Network Issue":
        case "Auto Launch Failed":
        case "Site Disconnect":
        case "Late Start Meeting":
        case "Late Start - Site":
        case "Site Unreachable / Powered Off":
        case "Communication Issue":
        case "Unable to Connect":
        case "Touch Panel Issue":
        case "Desktop Client Issue":
        case "VNOC Issue":
        case "VNOC System Issue":
        case "Meeting Scheduling Issue":
        case "Recertification / Non Meeting Issue":
        case "Manual Answer Issue":
        case "Endpoint Hardware Issue":
        case "Infrastructure Hardware Issue":
        case "Unmanaged Site Issue":
        case "Unknown":
        case "Other":
          return GraphType.Ping_Latency;
      }
    } else if (device.category == "IWB") {
      // Generic Surface Hub
      // 1 Ping Latency
      // 7 CPU
      // 8 Memory
      // 10 Network
      if (checkSubType("CPU Usage")) {
        return GraphType.Cpu;
      } else if (checkSubType("Memory Usage")) {
        return GraphType.Memory_In_Use;
      } else if (
        checkSubType("Bandwidth Usage (In)") ||
        checkSubType("Bandwidth Usage (Out)")
      ) {
        return GraphType.Network;
      }
      return GraphType.Ping_Latency;
    } else if (device.category == "MCU") {
      // 1 Ping Latency
      // 9 Ports
      if (ticket?.issue?.issue == "Port Utilization")
        return GraphType.Port_Usage_Video;
      else return GraphType.Ping_Latency;
    } else {
      return GraphType.Ping_Latency;
    }

    return GraphType.Ping_Latency;
  };

  const getTroubleshootGraphTitle = () => {
    let graphType = getTroubleshootGraph();
    switch (graphType) {
      case GraphType.Ping_Latency:
        return t("Ping Latency");
      case GraphType.Network:
        return t("Network");
      case GraphType.Cpu:
        return t("CPU");
      case GraphType.Frame_Rate:
        return t("Video Speed");
      case GraphType.Frame_Rate_Content:
        return t("Content Speed");
      case GraphType.Port_Usage_Video:
        return t("Ports");
      case GraphType.Jitter_Audio:
        return t("Audio Jitter");
      case GraphType.Jitter_Content:
        return t("Content Jitter");
      case GraphType.Jitter_Video:
        return t("Video Jitter");
      case GraphType.Memory_In_Use:
        return t("Memory");
      case GraphType.Packet_Loss:
        return t("Media Packet Loss (%)");
      case GraphType.Packet_Loss_Content:
        return t("Content Packet Loss (%)");
      default:
        return "";
    }
  };

  const getTroubleshotColor = () => {
    let graphType = getTroubleshootGraph();
    switch (graphType) {
      case GraphType.Ping_Latency:
        return GraphColor.pink;
      case GraphType.Network:
        return GraphColor.teal;
      case GraphType.Cpu:
      case GraphType.Frame_Rate:
      case GraphType.Frame_Rate_Content:
      case GraphType.Port_Usage_Video:
        return GraphColor.blue;
      case GraphType.Jitter_Audio:
      case GraphType.Jitter_Content:
      case GraphType.Jitter_Video:
        return GraphColor.mustard;
      case GraphType.Memory_In_Use:
        return GraphColor.purple;
      case GraphType.Packet_Loss:
      case GraphType.Packet_Loss_Content:
        return GraphColor.green;
      default:
        return GraphColor.pink;
    }
  };

  const { annotations: meetingAnnotations } = useMeetingAnnotations({
    device,
    start: getStartTime(),
    end: getStopTime(),
    liveUpdate: true,
    breadcrumbPage: "Tickets",
  });

  return (
    <Paper
      sx={(theme) => ({
        "&.MuiPaper-root": {
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.background.highlighter}`,
          padding: 2,
        },
      })}
      key={device.name}
    >
      <Grid container spacing={2} wrap="nowrap" sx={{ width: "100%" }}>
        <Grid
          container
          direction="column"
          sx={{
            justifyContent: "center",
            padding: 2,
            width: "15%",
          }}
        >
          <Typography
            size="small"
            sx={{
              wordBreak: "break-word",
            }}
          >
            {device.name}
          </Typography>
          <Link to={`/assets/devices/${device.id}`} target="_blank">
            <Button
              sx={{
                width: "100%",
                textDecoration: "none",
                marginTop: 3,
                textAlign: "center",
              }}
              color="white"
              variant="outlined"
            >
              Monitor
            </Button>
          </Link>
        </Grid>
        <Grid
          sx={{
            flex: "1",
          }}
        >
          {device.monitoringLevel === MonitoringLevel.None ? (
            <Typography>
              {t("This device is not managed by Symphony")}
            </Typography>
          ) : (
            <DeviceLiveChart
              color={getTroubleshotColor()}
              title={getTroubleshootGraphTitle()}
              graphType={getTroubleshootGraph()}
              device={device}
              start={getStartTime()}
              end={getStopTime()}
              meetingAnnotations={meetingAnnotations}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PaperDevice;
